/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 13th 2022, 5:41:38 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { PlainDialog } from "ui-widgets/dialogs/GenericDialogs";
import { useGlobalScope } from "core/providers";
import { ProgressWrapper, OSAlert } from "ui-widgets";
import { useNavigate } from "react-router";
import { resolveURLName } from "core/routes/utils";
import { ProcessDefinition } from "lib/schemas";

interface InstallationProgressComponentProps {
  serviceTitle: string;
  isAlreadyInstalled: boolean;
  installationProgressOpenState: boolean;
  isInstalling: boolean;
  hasInstallationError: boolean;
  installationError: string;
  isSuccessfulInstallation: boolean;
  closeInstallationProgress: () => void;
  installed_process_definition_id: ProcessDefinition["id"];
}

export const InstallationProgressComponent = (props: InstallationProgressComponentProps) => {
  const { progressTitle, selectedOrganization } = useInstallationProgressComponentLogic(props);

  const {
    installationProgressOpenState,
    isInstalling,
    hasInstallationError,
    installationError,
    isSuccessfulInstallation,
    isAlreadyInstalled,
    serviceTitle,
  } = props;

  return (
    <PlainDialog title={progressTitle} open={installationProgressOpenState} onClose={() => {}} fullWidth maxWidth="md">
      <ProgressWrapper showLoader={isInstalling} loaderType="linear">
        {(hasInstallationError || isSuccessfulInstallation) && (
          <OSAlert severity={hasInstallationError ? "error" : "success"} variant="outlined">
            {isSuccessfulInstallation ? (
              <>
                Successfully {isAlreadyInstalled ? "updated" : "installed"} <b>{serviceTitle}</b> service template for
                the organization named <b>{selectedOrganization.name}</b>
              </>
            ) : (
              installationError
            )}
          </OSAlert>
        )}
      </ProgressWrapper>
    </PlainDialog>
  );
};

const useInstallationProgressComponentLogic = ({
  closeInstallationProgress,
  installed_process_definition_id,
  isAlreadyInstalled,
  serviceTitle,
  isSuccessfulInstallation,
}: InstallationProgressComponentProps) => {
  const { selectedOrganization } = useGlobalScope().globalState;

  const progressTitle = `${isAlreadyInstalled ? "Updating" : "Installing"} ${serviceTitle}....`;

  const navigate = useNavigate();

  const handleGoToService = React.useCallback(() => {
    closeInstallationProgress();
    navigate(resolveURLName("serviceBuilderRoot", { serviceId: installed_process_definition_id }));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeInstallationProgress, installed_process_definition_id]);

  React.useEffect(() => {
    if (isSuccessfulInstallation && installed_process_definition_id) handleGoToService();
  }, [isSuccessfulInstallation, handleGoToService, installed_process_definition_id]);

  return { progressTitle, selectedOrganization };
};
